/**
 * Доска работы команды (/management/work): типы и чистая логика.
 *
 * Без обращений к сети — только то, что проверяется тестом (tests/work-board.test.ts):
 * раскладка карточек по секциям экрана и подпись «последняя активность».
 *
 * Модель упрощена 29.09.2026 (docs/TEAM_WORK_BOARD_PLAN.md, раздел «Упрощение»): карточки
 * ведёт лид раз в неделю через навык `work-sync`. С 09.10.2026 (миграция 20261009120000):
 * приоритет внутри «В работе» / «На очереди», «Доработка заказчика» (код `paused`),
 * «Приёмка заказчика» (`acceptance`) и анкета запроса по docs/WORK_REQUEST_RULES.md.
 */

export type WorkItemStatus = 'inbox' | 'queued' | 'in_progress' | 'paused' | 'acceptance' | 'done' | 'dropped'
export type WorkItemKind = 'feature' | 'bug' | 'research' | 'backend' | 'tech_debt' | 'incident' | 'support'
export type WorkItemSize = 'S' | 'M' | 'L'

export interface WorkItemRow {
  id: number
  title: string
  direction_code: string
  direction_title: string
  kind: WorkItemKind
  status: WorkItemStatus
  size: WorkItemSize | null
  requester: string | null
  request_text: string | null
  expected_result: string | null
  assignee_user_id: string | null
  assignee_name: string | null
  parent_id: number | null
  parent_title: string | null
  child_count: number
  child_done_count: number
  child_remaining_count: number
  child_assignee_names: string[]
  is_unplanned: boolean
  displaced_item_id: number | null
  next_step: string | null
  next_review_at: string | null
  blocked_reason: string | null
  blocked_waiting_on: string | null
  blocked_since: string | null
  created_at: string
  updated_at: string
  started_at: string | null
  done_at: string | null
  closed_at: string | null
  commit_count: number
  first_commit_at: string | null
  last_commit_at: string | null
  last_event_at: string | null
  last_event_note: string | null
  priority_rank: number | null
  acceptor_user_id: string | null
  acceptor_name: string | null
  created_by_user_id: string | null
  request_brief: WorkRequestBrief | null
}

export const STATUS_LABELS: Record<WorkItemStatus, string> = {
  inbox: 'Новая',
  queued: 'На очереди',
  in_progress: 'В работе',
  paused: 'Доработка заказчика',
  acceptance: 'Приёмка заказчика',
  done: 'Готово',
  dropped: 'Не будем',
}

/** Что статус значит для заказчика: одна фраза. */
export const STATUS_HINTS: Record<WorkItemStatus, string> = {
  inbox: 'Просьба записана, ещё не разобрали.',
  queued: 'На очереди: понятно, что делать, ждёт своей очереди. Срока пока нет.',
  in_progress: 'Делается сейчас.',
  paused: 'Вернули заказчику: ответить на вопросы или уточнить запрос. Что именно — в последней заметке.',
  acceptance: 'Команда сделала. Принимающий проверяет результат: принять или вернуть с замечанием.',
  done: 'Сделано, выложено и принято.',
  dropped: 'Делать не будем. Причина — в журнале.',
}

/** Порядок в выпадашке статуса — по ходу работы. */
export const STATUS_ORDER: WorkItemStatus[] = ['inbox', 'paused', 'queued', 'in_progress', 'acceptance', 'done', 'dropped']

export const isClosed = (status: WorkItemStatus): boolean => status === 'done' || status === 'dropped'

/** Смена на эти статусы требует заметки: заказчику надо понимать, что доработать или почему отказ. */
export const STATUS_NEEDS_NOTE: WorkItemStatus[] = ['paused', 'dropped']

/* ------------------------------------------------------------------ */
/* Даты                                                                */
/* ------------------------------------------------------------------ */

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]}`
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function daysSince(iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.floor((startOfDay(now).getTime() - startOfDay(new Date(t)).getTime()) / 86400000))
}

export function pluralDays(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'день'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'дня'
  return 'дней'
}

export function pluralCommits(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'коммит'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'коммита'
  return 'коммитов'
}

/* ------------------------------------------------------------------ */
/* Секции экрана                                                       */
/* ------------------------------------------------------------------ */

/** Самый поздний момент, когда с карточкой что-то происходило. */
export function lastActivityAt(item: Pick<WorkItemRow, 'last_commit_at' | 'last_event_at' | 'updated_at'>): string {
  return [item.last_commit_at, item.last_event_at, item.updated_at]
    .filter((v): v is string => Boolean(v))
    .reduce((a, b) => (a > b ? a : b))
}

/**
 * Подпись активности для строки: «последний коммит 23 сентября · всего 4 коммита».
 * Коммиты — честный сигнал «делаем» для заказчика; без них — дата последнего события.
 */
export function activityLabel(item: WorkItemRow, now: Date = new Date()): string {
  if (item.commit_count > 0 && item.last_commit_at) {
    const days = daysSince(item.last_commit_at, now)
    const when = days === 0 ? 'сегодня' : days === 1 ? 'вчера' : formatDate(item.last_commit_at)
    return `последний коммит ${when} · всего ${item.commit_count} ${pluralCommits(item.commit_count)}`
  }
  return `обновлено ${formatDate(lastActivityAt(item))}`
}

export type SectionKey = 'work' | 'inbox' | 'paused' | 'acceptance' | 'closed'

/** Блоки вкладки «Работа»: у каждого свой порядок приоритета. */
export type PriorityStatus = 'in_progress' | 'queued'
export const PRIORITY_STATUSES: PriorityStatus[] = ['in_progress', 'queued']

export interface BoardBlock {
  status: PriorityStatus
  title: string
  items: WorkItemRow[]
}

export interface BoardSection {
  key: SectionKey
  title: string
  hint: string
  items: WorkItemRow[]
  /** Только у «Работы»: «В работе» сверху, «На очереди» ниже, внутри — по приоритету. */
  blocks?: BoardBlock[]
}

const SECTION_META: Record<SectionKey, { title: string; hint: string }> = {
  work: { title: 'Работа', hint: 'Сверху — что делаем сейчас, ниже — очередь. Внутри блока — по приоритету.' },
  inbox: { title: 'Новые запросы', hint: 'Ещё не разобрали.' },
  paused: { title: 'Доработка заказчика', hint: 'Вернули заказчику: ответить на вопросы или уточнить запрос.' },
  acceptance: { title: 'Приёмка заказчика', hint: 'Сделано — ждёт проверки принимающего.' },
  closed: { title: 'Закрытые', hint: 'Готово и отклонено за выбранный срок.' },
}

export const SECTION_ORDER: SectionKey[] = ['work', 'inbox', 'paused', 'acceptance', 'closed']

/** Вкладка из `?tab=`. Старые ссылки `in_progress` / `queued` ведут во «Работу». */
export function sectionFromParam(value: string | null): SectionKey | null {
  if (value === 'in_progress' || value === 'queued') return 'work'
  return value !== null && (SECTION_ORDER as string[]).includes(value) ? (value as SectionKey) : null
}

export interface BoardFilter {
  /** Закрытые показываются, если закрыты не раньше этой даты. */
  closedSince: Date
  /** Поиск по названию и тексту просьбы. */
  query?: string
  /** Тег (направление); пусто — все. */
  direction?: string | null
}

function matches(item: WorkItemRow, query: string): boolean {
  if (!query) return true
  return [item.title, item.request_text, item.requester, item.assignee_name, item.acceptor_name, item.direction_title].some(value =>
    value?.toLocaleLowerCase('ru-RU').includes(query)
  )
}

/**
 * Порядок внутри блока приоритета — тот же, что у reorder_work_items на сервере:
 * ранг (без ранга — в конец), затем дата заведения, затем номер.
 */
export function comparePriority(a: WorkItemRow, b: WorkItemRow): number {
  if (a.priority_rank !== b.priority_rank) {
    if (a.priority_rank === null) return 1
    if (b.priority_rank === null) return -1
    return a.priority_rank - b.priority_rank
  }
  return a.created_at.localeCompare(b.created_at) || a.id - b.id
}

/**
 * Раскладка карточек по вкладкам экрана.
 * «Работа» — два блока по приоритету; новые, доработка и приёмка — по свежести активности;
 * закрытые — по дате закрытия. Пустые секции возвращаются тоже.
 */
export function buildBoardSections(items: WorkItemRow[], filter: BoardFilter): BoardSection[] {
  const query = filter.query?.trim().toLocaleLowerCase('ru-RU') ?? ''
  const since = filter.closedSince.getTime()
  const visible = items.filter(item => matches(item, query) && (!filter.direction || item.direction_code === filter.direction))

  const byStatus: Record<WorkItemStatus, WorkItemRow[]> = {
    inbox: [], queued: [], in_progress: [], paused: [], acceptance: [], done: [], dropped: [],
  }
  const closed: WorkItemRow[] = []
  for (const item of visible) {
    if (isClosed(item.status)) {
      if (item.closed_at && new Date(item.closed_at).getTime() >= since) closed.push(item)
    } else {
      byStatus[item.status].push(item)
    }
  }

  const byActivity = (a: WorkItemRow, b: WorkItemRow) => lastActivityAt(b).localeCompare(lastActivityAt(a))
  const blocks: BoardBlock[] = PRIORITY_STATUSES.map(status => ({
    status,
    title: STATUS_LABELS[status],
    items: [...byStatus[status]].sort(comparePriority),
  }))

  return SECTION_ORDER.map(key => {
    if (key === 'work') return { key, ...SECTION_META[key], items: blocks.flatMap(b => b.items), blocks }
    const list =
      key === 'closed'
        ? closed.sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? ''))
        : byStatus[key].sort(byActivity)
    return { key, ...SECTION_META[key], items: list }
  })
}

/** Новый порядок блока после перемещения карточки на шаг выше/ниже или в начало; null — двигать некуда. */
export function moveInOrder(ids: number[], id: number, move: 'up' | 'down' | 'top'): number[] | null {
  const from = ids.indexOf(id)
  if (from < 0) return null
  const to = move === 'top' ? 0 : move === 'up' ? from - 1 : from + 1
  if (to < 0 || to >= ids.length || to === from) return null
  const next = ids.filter(x => x !== id)
  next.splice(to, 0, id)
  return next
}

export interface DirectionTag {
  code: string
  title: string
  count: number
}

/** Теги для фильтра: направления карточек, частые первыми, «Прочее» последним. */
export function directionTags(items: WorkItemRow[]): DirectionTag[] {
  const tags = new Map<string, DirectionTag>()
  for (const item of items) {
    const code = item.direction_code || 'other'
    const tag = tags.get(code) ?? { code, title: item.direction_title || 'Прочее', count: 0 }
    tag.count += 1
    tags.set(code, tag)
  }
  return [...tags.values()].sort(
    (a, b) => Number(a.code === 'other') - Number(b.code === 'other') || b.count - a.count || a.title.localeCompare(b.title, 'ru')
  )
}

/* ------------------------------------------------------------------ */
/* Анкета запроса (docs/WORK_REQUEST_RULES.md)                          */
/* ------------------------------------------------------------------ */

export type BriefQuestionKey =
  | 'problem'
  | 'users'
  | 'frequency'
  | 'action'
  | 'business_result'
  | 'evidence'
  | 'cheaper_check'
  | 'success_check'

export type BriefException = 'incident' | 'security' | 'legal' | 'external'

export type WorkRequestBrief = Partial<Record<BriefQuestionKey, string>> & {
  exception?: BriefException
  deadline?: string
}

export interface BriefQuestion {
  key: BriefQuestionKey
  title: string
  hint: string
  placeholder: string
}

/**
 * Вопросы в порядке правил постановки. Вопрос «кто владелец результата» — выбор
 * принимающего в форме, отдельного текстового поля у него нет.
 * Серверная проверка — public.work_request_normalize_brief (каждый ответ ≥ 10 символов).
 */
export const BRIEF_QUESTIONS: BriefQuestion[] = [
  {
    key: 'problem',
    title: 'Какую проблему решаем?',
    hint: 'Не «нужен экран или отчёт», а что сегодня невозможно, неудобно или дорого делать без него.',
    placeholder: 'Опишите текущую ситуацию: что именно не получается, сколько времени или ошибок это создаёт.',
  },
  {
    key: 'users',
    title: 'Кто конкретно будет пользоваться и сколько их?',
    hint: 'Роль или группа: бухгалтерия УК, ТУ, педотдел, юристы, франчайзи… и примерное число людей.',
    placeholder: 'Укажите роли, команды или группы пользователей и примерное количество людей.',
  },
  {
    key: 'frequency',
    title: 'Как часто возникает эта потребность?',
    hint: 'Несколько раз в день, раз в неделю, раз в месяц, несколько раз в год.',
    placeholder: 'Укажите регулярность или событие, после которого возникает потребность.',
  },
  {
    key: 'action',
    title: 'Что человек сделает по результату?',
    hint: 'Если это аналитика — какое решение принимается после просмотра и что происходит, когда показатель изменился.',
    placeholder: 'Опишите решение или действие, которое последует после использования результата.',
  },
  {
    key: 'business_result',
    title: 'Что изменится для бизнеса?',
    hint: 'Измеримо: часы работы, число ошибок, выручка, конверсия, удержание, качество, скорость реакции.',
    placeholder: 'Укажите, какой показатель, срок или качество работы должны измениться.',
  },
  {
    key: 'evidence',
    title: 'Почему считаем, что это сработает?',
    hint: 'Ручной процесс уже есть, статистика, обращения франчайзи, реальные случаи, наблюдения сотрудников. Доказательств мало — сначала проверяем гипотезу.',
    placeholder: 'Опишите наблюдения, обращения, статистику или существующий ручной процесс.',
  },
  {
    key: 'cheaper_check',
    title: 'Можно ли проверить потребность дешевле?',
    hint: 'Google-таблица, ручной отчёт, разовая выгрузка, простой прототип. Особенно если сам процесс новый.',
    placeholder: 'Напишите, что уже пробовали без разработки и почему этого оказалось недостаточно.',
  },
  {
    key: 'success_check',
    title: 'Как через 30–60 дней поймём, что разработка была оправдана?',
    hint: 'Кто пользуется, насколько регулярно, изменился ли показатель, ради которого делали.',
    placeholder: 'Укажите измеримые признаки, по которым можно будет проверить пользу результата.',
  },
]

export const BRIEF_EXCEPTION_LABELS: Record<BriefException, string> = {
  incident: 'Авария',
  security: 'Безопасность',
  legal: 'Юридическое требование',
  external: 'Внешнее обязательство',
}

export const BRIEF_MIN_ANSWER = 10

/** Ошибки формы до отправки — зеркало серверной проверки (решает сервер). */
export function validateBrief(brief: WorkRequestBrief): Partial<Record<BriefQuestionKey, string>> {
  const errors: Partial<Record<BriefQuestionKey, string>> = {}
  for (const q of BRIEF_QUESTIONS) {
    if (brief.exception && q.key !== 'problem') continue
    if ((brief[q.key]?.trim().length ?? 0) < BRIEF_MIN_ANSWER) {
      errors[q.key] = 'Ответьте хотя бы одной фразой'
    }
  }
  return errors
}
